import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';
import ConversationViewer from './ConversationViewer';
import ManualLeadForm from './ManualLeadForm';
import BulkLeadImport from './BulkLeadImport';
import SegmentManagementModal from './SegmentManagementModal';
import segmentService from '../../services/segmentService';
import { BulkImportResponse } from '../../services/leadImportService';
import QuizAnswersSection from './QuizAnswersSection';

interface Child {
  name: string;
  age?: number;
  grade?: string;
  special_needs: string[];
  current_support?: string[];
}

interface Lead {
  id: number;
  email: string;
  phone?: string;
  parent_name?: string;
  children?: Child[];
  // Legacy fields for backward compatibility
  child_name?: string;
  child_age?: number;
  grade?: string;
  special_needs?: string[];
  current_support?: string[];
  timeline: string;
  timeline_date?: string; // ISO date string for specific date selection
  biggest_concerns?: string;
  segment_tags: string[];
  lead_score: number;
  source?: string; // Lead source: 'chatbot', 'enroll_form', 'newsletter_form', etc.
  source_url?: string;
  created_at: string;
  updated_at: string;
  // Conversion tracking fields
  conversion_status?: string; // 'pending', 'enrolled', 'not_interested'
  enrollment_date?: string; // ISO date string when they became a client
  payment_amount?: number; // Amount paid in dollars
  payment_period?: string; // Text description of what period payment covers
  // Email subscription status fields
  email_status?: string; // 'subscribed', 'unsubscribed', 'bounced', 'not_subscribed', 'complained'
  unsubscribed_at?: string; // ISO date string when they unsubscribed
  unsubscribe_reason?: string; // Reason for unsubscribing
  // Quiz-specific fields
  quiz_profile_id?: number;
  quiz_dimension_scores?: string; // JSON string with dimension scores
  quiz_score?: number;
  quiz_profile?: {
    id: number;
    slug: string;
    name: string;
    tagline: string;
  };
}

interface Segment {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  never_contact: boolean;
}

interface ColumnGroup {
  group: string;
  columns: string[];
}

interface LeadsResponse {
  leads: Lead[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const LeadsTable: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScore, setFilterScore] = useState('all');
  const [filterTimeline, setFilterTimeline] = useState('all');
  const [filterSegment, setFilterSegment] = useState('all');
  const [filterConversionStatus, setFilterConversionStatus] = useState('all');
  const [filterEmailStatus, setFilterEmailStatus] = useState('all');
  const [filterQuizProfile, setFilterQuizProfile] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<LeadsResponse['pagination'] | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; leadId: number | null }>({
    show: false,
    leadId: null
  });
  const [showConversationViewer, setShowConversationViewer] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [bulkSegmentId, setBulkSegmentId] = useState<string>('');
  const [isAssigningSegments, setIsAssigningSegments] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isQuizInsightsExpanded, setIsQuizInsightsExpanded] = useState(false);

  // Widget settings for quiz components
  const [widgetSettings, setWidgetSettings] = useState({
    school_name: 'Armenta Learning Academy',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#10B981'
  });

  // Import modal states
  const [showManualLeadForm, setShowManualLeadForm] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);

  // Segment management modal state
  const [segmentModalLead, setSegmentModalLead] = useState<Lead | null>(null);

  // Edit modal states
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Export modal states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [availableColumns, setAvailableColumns] = useState<ColumnGroup[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [showColumnSelection, setShowColumnSelection] = useState(false);

  // Available options for editing (reused from ManualLeadForm)
  const timelineOptions = [
    'Immediately',
    'Next semester',
    'Next school year',
    'Just exploring'
  ];

  const specialNeedsOptions = [
    'Autism/ASD',
    'ADHD',
    'Learning Disabilities',
    'Dyslexia',
    'Speech/Language Delays',
    'Sensory Processing',
    'Anxiety/Depression',
    'Physical Disabilities',
    'Intellectual Disabilities',
    'Other'
  ];

  const currentSupportOptions = [
    'IEP',
    '504 Plan',
    'Speech therapy',
    'Occupational Therapy (OT)',
    'Physical Therapy (PT)',
    'Behavioral therapy',
    'Tutoring',
    'Shadow aide',
    'Special education services',
    'None currently'
  ];

  const supportServicesOptions = currentSupportOptions;

  // Helper function to get all children for a lead (handles both new and legacy format)
  const getChildren = (lead: Lead): Child[] => {
    if (lead.children && lead.children.length > 0) {
      return lead.children;
    } else if (lead.child_name || lead.child_age || lead.special_needs?.length) {
      // Legacy single child format
      return [{
        name: lead.child_name || 'Unknown Child',
        age: lead.child_age,
        grade: lead.grade,
        special_needs: lead.special_needs || [],
        current_support: lead.current_support || []
      }];
    }
    return [];
  };

  // Helper function to aggregate all special needs from all children
  const getAllSpecialNeeds = (lead: Lead): string[] => {
    const children = getChildren(lead);
    const allNeeds: string[] = [];
    children.forEach(child => {
      if (child.special_needs) {
        allNeeds.push(...child.special_needs);
      }
    });
    return Array.from(new Set(allNeeds)); // Remove duplicates
  };

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      
      console.log(`📄 Fetching leads - Page: ${currentPage}, Filters:`, {
        searchTerm,
        filterScore,
        filterTimeline,
        filterSegment,
        filterConversionStatus,
        filterEmailStatus,
        filterQuizProfile
      });

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(filterScore !== 'all' && { score: filterScore }),
        ...(filterTimeline !== 'all' && { timeline: filterTimeline }),
        ...(filterSegment !== 'all' && { segment: filterSegment }),
        ...(filterConversionStatus !== 'all' && { conversion_status: filterConversionStatus }),
        ...(filterEmailStatus !== 'all' && { email_status: filterEmailStatus }),
        ...(filterQuizProfile !== 'all' && { quiz_profile: filterQuizProfile })
      });

      const response = await authService.makeAuthenticatedRequest(
        `${process.env.REACT_APP_API_URL}/api/leads?${params}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch leads');
      }

      const data: LeadsResponse = await response.json();
      
      console.log(`📊 Pagination data:`, data.pagination);
      
      // Handle edge case where current page is beyond available pages
      if (data.pagination.totalPages > 0 && currentPage > data.pagination.totalPages) {
        console.log(`⚠️ Current page ${currentPage} > total pages ${data.pagination.totalPages}, resetting to page 1`);
        setCurrentPage(1);
        return; // This will trigger another fetch with page 1
      }
      
      setLeads(data.leads);
      setPagination(data.pagination);
    } catch (err) {
      console.error('❌ Error fetching leads:', err);
      setError(err instanceof Error ? err.message : 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterScore, filterTimeline, filterSegment, filterConversionStatus, filterEmailStatus, filterQuizProfile]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    fetchSegments();
  }, []); // Only fetch segments once on mount

  // Fetch widget settings for quiz components
  useEffect(() => {
    const fetchWidgetSettings = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_BASE_URL}/widget/config`);
        if (response.ok) {
          const data = await response.json();
          setWidgetSettings(data);
        }
      } catch (error) {
        console.error('Failed to fetch widget settings:', error);
        // Keep default settings if fetch fails
      }
    };
    fetchWidgetSettings();
  }, []); // Only fetch once on mount

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      console.log('🔄 Filter changed, resetting to page 1');
      setCurrentPage(1);
    }
  }, [searchTerm, filterScore, filterTimeline, filterSegment, filterConversionStatus, filterEmailStatus, filterQuizProfile, currentPage]);

  const fetchSegments = async () => {
    try {
      const segmentList = await segmentService.getSegments();
      setSegments(segmentList.filter(s => s.is_active && !s.never_contact));
    } catch (err) {
      console.error('Failed to fetch segments:', err);
    }
  };

  const handleBulkSegmentAssignment = async () => {
    if (!bulkSegmentId || selectedLeadIds.length === 0) return;
    
    try {
      setIsAssigningSegments(true);
      const result = await segmentService.assignLeadsToSegment(Number(bulkSegmentId), selectedLeadIds);
      if (result.success) {
        setSelectedLeadIds([]);
        setBulkSegmentId('');
        await fetchLeads();
        setSuccessMessage(`${result.assigned} leads assigned to segment successfully`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      alert('Failed to assign leads to segment');
    } finally {
      setIsAssigningSegments(false);
    }
  };

  const toggleLeadSelection = (leadId: number) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const selectAllLeads = () => {
    if (selectedLeadIds.length === leads.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(leads.map(l => l.id));
    }
  };

  // Import handlers
  const handleManualLeadSuccess = (lead: any) => {
    console.log('✅ Manual lead created successfully:', lead);
    setShowManualLeadForm(false);
    setSuccessMessage(`Lead "${lead.parent_name}" created successfully!`);
    setTimeout(() => setSuccessMessage(null), 5000);

    // Refresh the leads table to show the new lead
    fetchLeads();
  };

  const handleBulkImportSuccess = (results: BulkImportResponse) => {
    console.log('✅ Bulk import completed:', results);
    setShowBulkImportModal(false);

    const { created, skipped, errors } = results.results;
    let message = `Import completed: ${created} leads created`;
    if (skipped > 0) message += `, ${skipped} skipped`;
    if (errors > 0) message += `, ${errors} errors`;

    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 8000);

    // Refresh the leads table to show the new leads
    fetchLeads();
  };

  const handleDeleteLead = async (leadId: number) => {
    try {
      setDeleting(true);
      const response = await authService.makeAuthenticatedRequest(
        `${process.env.REACT_APP_API_URL}/api/leads/${leadId}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete lead');
      }

      // Remove lead from local state
      setLeads(leads.filter(lead => lead.id !== leadId));
      
      // Update pagination count
      if (pagination) {
        setPagination({
          ...pagination,
          total: pagination.total - 1
        });
      }

      setDeleteConfirm({ show: false, leadId: null });
      
      // Show success message (you can replace with a toast notification)
      alert('Lead deleted successfully');
    } catch (err) {
      alert('Failed to delete lead: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  // Fetch available export columns
  const fetchAvailableColumns = async () => {
    try {
      const token = authService.getToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leads/export/columns`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available columns');
      }

      const data = await response.json();
      setAvailableColumns(data.columns);

      // Set all columns as selected by default
      const allColumns = data.columns.flatMap((group: ColumnGroup) => group.columns);
      setSelectedColumns(allColumns);
    } catch (error) {
      console.error('Error fetching available columns:', error);
    }
  };

  // Handle column selection toggle
  const handleColumnToggle = (column: string) => {
    setSelectedColumns(prev =>
      prev.includes(column)
        ? prev.filter(col => col !== column)
        : [...prev, column]
    );
  };

  // Handle group selection toggle
  const handleGroupToggle = (group: ColumnGroup) => {
    const groupColumns = group.columns;
    const allSelected = groupColumns.every(col => selectedColumns.includes(col));

    if (allSelected) {
      // Deselect all columns in group
      setSelectedColumns(prev => prev.filter(col => !groupColumns.includes(col)));
    } else {
      // Select all columns in group
      setSelectedColumns(prev => {
        const newColumns = [...prev];
        groupColumns.forEach(col => {
          if (!newColumns.includes(col)) {
            newColumns.push(col);
          }
        });
        return newColumns;
      });
    }
  };

  // Handle export button click
  const handleExportLeads = () => {
    setShowExportModal(true);
    // Fetch columns when modal opens
    fetchAvailableColumns();
  };

  // Handle actual export
  const performExport = async () => {
    try {
      setIsExporting(true);

      // Build query parameters
      const params = new URLSearchParams({
        format: exportFormat
      });

      // Add selected columns if any are specified (for CSV format)
      if (exportFormat === 'csv' && selectedColumns.length > 0) {
        params.append('columns', selectedColumns.join(','));
      }

      const response = await authService.makeAuthenticatedRequest(
        `${process.env.REACT_APP_API_URL}/api/leads/export/comprehensive?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error('Failed to export leads');
      }

      if (exportFormat === 'json') {
        // Handle JSON download
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        // Handle CSV download
        const csv = await response.text();
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `comprehensive-lead-export-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      setShowExportModal(false);
      setSuccessMessage(`Successfully exported all leads as ${exportFormat.toUpperCase()}`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      alert('Failed to export leads: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsExporting(false);
    }
  };

  const openLeadDetail = (lead: Lead) => {
    setSelectedLead(lead);
    setIsEditing(false);
    setEditFormData(null);
    setShowDetailModal(true);
  };

  // Edit mode handlers
  const startEditing = () => {
    if (!selectedLead) return;

    // Initialize edit form data
    const formData = {
      email: selectedLead.email,
      parent_name: selectedLead.parent_name,
      phone: selectedLead.phone || '',
      timeline: selectedLead.timeline || 'Just exploring',
      timeline_date: selectedLead.timeline_date || '',
      source: selectedLead.source || 'chatbot',
      biggest_concerns: selectedLead.biggest_concerns || '',
      children: getChildren(selectedLead).length > 0 ? getChildren(selectedLead) : [{
        name: '',
        age: undefined,
        grade: '',
        special_needs: [],
        current_support: []
      }],
      // Conversion tracking fields
      conversion_status: selectedLead.conversion_status || 'pending',
      enrollment_date: selectedLead.enrollment_date || '',
      payment_amount: selectedLead.payment_amount || '',
      payment_period: selectedLead.payment_period || ''
    };

    setEditFormData(formData);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditFormData(null);
  };

  const handleSaveChanges = async () => {
    if (!selectedLead || !editFormData) return;

    try {
      setIsSaving(true);

      console.log('💾 Saving lead changes:', editFormData);

      const response = await authService.makeAuthenticatedRequest(
        `${process.env.REACT_APP_API_URL}/api/leads/${selectedLead.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(editFormData),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update lead');
      }

      const result = await response.json();
      console.log('✅ Lead updated:', result);

      // Update the selected lead with new data
      setSelectedLead(result.lead);
      setIsEditing(false);
      setEditFormData(null);

      // Show success message
      setSuccessMessage(`Lead "${result.lead.parent_name}" updated successfully!`);
      setTimeout(() => setSuccessMessage(null), 5000);

      // Refresh the leads table to show updated data
      await fetchLeads();

    } catch (error) {
      console.error('❌ Failed to update lead:', error);
      alert('Failed to update lead: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Form change handlers
  const handleEditInputChange = (field: string, value: any) => {
    setEditFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const addChild = () => {
    setEditFormData((prev: any) => ({
      ...prev,
      children: [
        ...prev.children,
        {
          name: '',
          age: undefined,
          grade: '',
          special_needs: [],
          current_support: []
        }
      ]
    }));
  };

  const removeChild = (childIndex: number) => {
    setEditFormData((prev: any) => ({
      ...prev,
      children: prev.children.filter((_: any, index: number) => index !== childIndex)
    }));
  };

  const updateChild = (childIndex: number, field: string, value: any) => {
    setEditFormData((prev: any) => ({
      ...prev,
      children: prev.children.map((child: any, index: number) =>
        index === childIndex ? { ...child, [field]: value } : child
      )
    }));
  };

  const getScoreBadgeColor = (score: number) => {
    if (score >= 70) return 'bg-green-100 text-green-800';
    if (score >= 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getConversionStatusBadge = (status?: string) => {
    switch (status) {
      case 'enrolled':
        return (
          <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-300">
            ✅ Enrolled
          </span>
        );
      case 'interested':
        return (
          <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 border border-blue-300">
            💡 Interested
          </span>
        );
      case 'contacted':
        return (
          <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300">
            📞 Contacted
          </span>
        );
      case 'lost':
        return (
          <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-300">
            ❌ Lost
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-300">
            ⏳ Pending
          </span>
        );
    }
  };

  const getEmailStatusBadge = (status?: string) => {
    switch (status) {
      case 'subscribed':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            ✅ Subscribed
          </span>
        );
      case 'unsubscribed':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
            ❌ Unsubscribed
          </span>
        );
      case 'bounced':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
            ⚠️ Bounced
          </span>
        );
      case 'not_subscribed':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            ⏸️ Not Subscribed
          </span>
        );
      case 'complained':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            🚫 Complained
          </span>
        );
      default:
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            ✅ Subscribed
          </span>
        );
    }
  };

  const getSourceBadge = (source?: string) => {
    switch (source) {
      case 'chatbot':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-300">
            💬 Chatbot
          </span>
        );
      case 'quiz':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 border border-orange-300">
            🎯 Quiz
          </span>
        );
      case 'enroll_form':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-300">
            📝 Contact Form
          </span>
        );
      case 'newsletter_form':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 border border-purple-300">
            📧 Newsletter
          </span>
        );
      case 'manual':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300">
            ✍️ Manual Entry
          </span>
        );
      default:
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 border border-gray-300">
            ❓ Unknown
          </span>
        );
    }
  };

  // Quiz profile badge renderer with icons
  const getQuizProfileBadge = (lead: Lead) => {
    // Only show for quiz leads
    if (lead.source !== 'quiz' || !lead.quiz_profile) {
      return <span className="text-sm text-gray-400">-</span>;
    }

    const profileConfig: Record<string, { icon: string; bg: string; text: string; border: string }> = {
      'safety-first': {
        icon: '🛡️',
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-300'
      },
      'structured': {
        icon: '🏗️',
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-300'
      },
      'sensory-sensitive': {
        icon: '🧩',
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        border: 'border-purple-300'
      },
      'individualized-pace': {
        icon: '📚',
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-300'
      },
      'interest-driven': {
        icon: '🎨',
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-300'
      }
    };

    const config = profileConfig[lead.quiz_profile.slug] || {
      icon: '📋',
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-300'
    };

    // Extract short name (e.g., "The Structured Learner" → "Structured")
    const shortName = lead.quiz_profile.name.replace('The ', '').replace(' Learner', '');

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text} border ${config.border}`}>
        <span className="mr-1">{config.icon}</span>
        {shortName}
      </span>
    );
  };

  // Parse quiz dimension scores from JSON string
  const parseQuizDimensionScores = (jsonString?: string) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Failed to parse quiz dimension scores:', error);
      return null;
    }
  };

  // Format specific date for display
  const formatTimelineDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Calculate timeline urgency from specific date
  const calculateDateUrgency = (dateString: string) => {
    const selectedDate = new Date(dateString + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const daysUntilStart = Math.ceil((selectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilStart < 30) return 'immediate';
    if (daysUntilStart < 90) return 'few-months';
    if (daysUntilStart < 180) return 'six-months';
    if (daysUntilStart < 365) return 'next-year';
    return 'future';
  };

  const getTimelineBadgeColor = (timeline: string, timelineDate?: string) => {
    // If we have a specific date, use date-based urgency
    if (timelineDate) {
      const urgency = calculateDateUrgency(timelineDate);
      switch (urgency) {
        case 'immediate':
          return 'bg-red-100 text-red-800 border border-red-300'; // Highest priority - urgent
        case 'few-months':
          return 'bg-orange-100 text-orange-800 border border-orange-300'; // High priority
        case 'six-months':
          return 'bg-yellow-100 text-yellow-800 border border-yellow-300'; // Medium-high priority
        case 'next-year':
          return 'bg-blue-100 text-blue-800 border border-blue-300'; // Medium priority
        case 'future':
          return 'bg-indigo-100 text-indigo-800 border border-indigo-300'; // Future planning
        default:
          return 'bg-gray-100 text-gray-800 border border-gray-300';
      }
    }

    // Handle null/undefined timeline (e.g., from newsletter/contact form submissions)
    if (!timeline) {
      return 'bg-gray-100 text-gray-800 border border-gray-300'; // Unknown timeline
    }

    // Fallback to text-based priority
    const lowerTimeline = timeline.toLowerCase();
    if (lowerTimeline.includes('immediately') || lowerTimeline.includes('immediate') || lowerTimeline.includes('asap')) {
      return 'bg-red-100 text-red-800 border border-red-300'; // Highest priority - urgent
    }
    if (lowerTimeline.includes('next semester') || lowerTimeline.includes('semester') || lowerTimeline.includes('january')) {
      return 'bg-orange-100 text-orange-800 border border-orange-300'; // High priority
    }
    if (lowerTimeline.includes('next year') || lowerTimeline.includes('school year') || lowerTimeline.includes('fall')) {
      return 'bg-yellow-100 text-yellow-800 border border-yellow-300'; // Medium priority
    }
    if (lowerTimeline.includes('exploring') || lowerTimeline.includes('researching') || lowerTimeline.includes('just looking')) {
      return 'bg-blue-100 text-blue-800 border border-blue-300'; // Lower priority but still engaged
    }
    return 'bg-gray-100 text-gray-800 border border-gray-300'; // Unknown timeline
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Leads Management</h2>
          <p className="text-gray-600">
            {pagination ? `${pagination.total} total leads` : 'No leads found'}
            {selectedLeadIds.length > 0 && ` · ${selectedLeadIds.length} selected`}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Import/Export Buttons */}
          <div className="flex gap-2 mb-2 sm:mb-0">
            <button
              onClick={() => setShowManualLeadForm(true)}
              className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
              title="Add single lead manually"
            >
              ➕ Add Lead
            </button>
            <button
              onClick={() => setShowBulkImportModal(true)}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
              title="Import multiple leads from CSV"
            >
              📥 Bulk Import
            </button>
            <button
              onClick={handleExportLeads}
              className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
              title="Export all leads with complete data"
            >
              📤 Export All
            </button>
          </div>

          {/* Filter Controls */}
          <input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <select
            value={filterScore}
            onChange={(e) => setFilterScore(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Scores</option>
            <option value="high">High (70+)</option>
            <option value="medium">Medium (40-69)</option>
            <option value="low">Low (0-39)</option>
          </select>
          <select
            value={filterTimeline}
            onChange={(e) => setFilterTimeline(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Timelines</option>
            <option value="Immediately">🔴 Immediate</option>
            <option value="Next semester">🟠 Next Semester</option>
            <option value="Next school year">🟡 Next School Year</option>
            <option value="Just exploring">🔵 Exploring</option>
            <option value="unknown">❓ Unknown</option>
          </select>
          <select
            value={filterSegment}
            onChange={(e) => setFilterSegment(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Segments</option>
            <option value="uncategorized">🔍 Uncategorized</option>
            {segments.map(segment => (
              <option key={segment.id} value={segment.name}>
                📂 {segment.name}
              </option>
            ))}
          </select>
          <select
            value={filterConversionStatus}
            onChange={(e) => setFilterConversionStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="pending">⏳ Pending</option>
            <option value="enrolled">✅ Enrolled</option>
            <option value="not_interested">❌ Not Interested</option>
          </select>
          <select
            value={filterEmailStatus}
            onChange={(e) => setFilterEmailStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Email Status</option>
            <option value="subscribed">✅ Subscribed</option>
            <option value="unsubscribed">❌ Unsubscribed</option>
            <option value="bounced">⚠️ Bounced</option>
            <option value="complained">🚫 Complained</option>
          </select>
          <select
            value={filterQuizProfile}
            onChange={(e) => setFilterQuizProfile(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Profiles</option>
            <option value="safety-first">🛡️ Safety-First</option>
            <option value="structured">🏗️ Structured</option>
            <option value="sensory-sensitive">🧩 Sensory-Sensitive</option>
            <option value="individualized-pace">📚 Individualized-Pace</option>
            <option value="interest-driven">🎨 Interest-Driven</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedLeadIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-blue-700 font-medium">
              {selectedLeadIds.length} lead{selectedLeadIds.length > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedLeadIds([])}
              className="text-blue-600 hover:text-blue-800 underline text-sm"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={bulkSegmentId}
              onChange={(e) => setBulkSegmentId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select segment...</option>
              {segments.map(segment => (
                <option key={segment.id} value={segment.id}>
                  {segment.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleBulkSegmentAssignment}
              disabled={!bulkSegmentId || isAssigningSegments}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAssigningSegments ? 'Assigning...' : 'Assign to Segment'}
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-green-600">✅</span>
            <span className="text-green-700 font-medium">{successMessage}</span>
          </div>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800"
          >
            ✕
          </button>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3">
                  <input
                    type="checkbox"
                    checked={selectedLeadIds.length === leads.length && leads.length > 0}
                    onChange={selectAllLeads}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quiz Profile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Child Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Special Needs
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timeline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Segments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-2 py-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.includes(lead.id)}
                      onChange={() => toggleLeadSelection(lead.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => openLeadDetail(lead)}>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {lead.parent_name || 'Unknown Parent'}
                      </div>
                      <div className="text-sm text-gray-500">{lead.email}</div>
                      {lead.phone && (
                        <div className="text-sm text-gray-500">{lead.phone}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getSourceBadge(lead.source)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getQuizProfileBadge(lead)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      {getChildren(lead).map((child, childIndex) => (
                        <div key={childIndex} className={childIndex > 0 ? 'border-t pt-2' : ''}>
                          <div className="text-sm font-medium text-gray-900">
                            {child.name}
                            {getChildren(lead).length > 1 && (
                              <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                Child {childIndex + 1}
                              </span>
                            )}
                          </div>
                          {child.age && (
                            <div className="text-sm text-gray-500">
                              Age: {child.age}
                            </div>
                          )}
                          {child.grade && (
                            <div className="text-sm text-gray-500">
                              Grade: {child.grade}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {getAllSpecialNeeds(lead).map((need, index) => (
                        <span
                          key={index}
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800"
                        >
                          {need}
                        </span>
                      ))}
                      {getChildren(lead).length > 1 && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {getChildren(lead).length} Children
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTimelineBadgeColor(lead.timeline, lead.timeline_date)}`}>
                      {lead.timeline_date ? formatTimelineDate(lead.timeline_date) : lead.timeline}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreBadgeColor(lead.lead_score)}`}>
                      {lead.lead_score}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className="flex flex-wrap gap-1 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSegmentModalLead(lead);
                      }}
                      title="Click to manage segments"
                    >
                      {lead.segment_tags && lead.segment_tags.length > 0 ? (
                        lead.segment_tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <button
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 border border-purple-300 rounded-full hover:bg-purple-50 transition-colors"
                        >
                          + Add Segment
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getEmailStatusBadge(lead.email_status || 'subscribed')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(lead.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openLeadDetail(lead);
                        }}
                        className="text-blue-600 hover:text-blue-900 px-2 py-1 rounded border border-blue-300 hover:bg-blue-50"
                        title="View Details"
                      >
                        👁️ View
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowConversationViewer(lead.id);
                        }}
                        className="text-purple-600 hover:text-purple-900 px-2 py-1 rounded border border-purple-300 hover:bg-purple-50"
                        title="View Conversations"
                      >
                        💬 Chat
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm({ show: true, leadId: lead.id });
                        }}
                        className="text-red-600 hover:text-red-900 px-2 py-1 rounded border border-red-300 hover:bg-red-50"
                        title="Delete Lead"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {Math.max(1, ((pagination.page - 1) * pagination.limit) + 1)} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const newPage = currentPage - 1;
                console.log(`⬅️ Previous button clicked: ${currentPage} -> ${newPage}`);
                if (newPage >= 1) {
                  setCurrentPage(newPage);
                }
              }}
              disabled={!pagination || currentPage === 1 || loading}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="flex items-center px-3 py-2 text-sm text-gray-700">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => {
                const newPage = currentPage + 1;
                console.log(`➡️ Next button clicked: ${currentPage} -> ${newPage}, Total Pages: ${pagination.totalPages}`);
                if (newPage <= pagination.totalPages) {
                  setCurrentPage(newPage);
                }
              }}
              disabled={!pagination || currentPage >= pagination.totalPages || loading}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Lead</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this lead? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setDeleteConfirm({ show: false, leadId: null })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteConfirm.leadId && handleDeleteLead(deleteConfirm.leadId)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {isEditing ? 'Edit Lead' : 'Lead Profile'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {isEditing ? 'Modify lead information and children details' : 'Comprehensive lead information and analysis'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!isEditing ? (
                    <button
                      onClick={startEditing}
                      className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                    >
                      ✏️ Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={cancelEditing}
                        disabled={isSaving}
                        className="px-3 py-1 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveChanges}
                        disabled={isSaving}
                        className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                      >
                        {isSaving ? 'Saving...' : '💾 Save'}
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors duration-200"
                    disabled={isSaving}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Parent Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-sm">👤</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Parent Information</h3>
                    <p className="text-xs text-gray-500">Contact details and preferences</p>
                  </div>
                </div>

                {!isEditing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Full Name</label>
                      <p className="text-lg font-medium text-gray-900">{selectedLead.parent_name || 'Not provided'}</p>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Email Address</label>
                      <p className="text-lg font-medium text-gray-900">{selectedLead.email}</p>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Phone Number</label>
                      <p className="text-lg font-medium text-gray-900">{selectedLead.phone || 'Not provided'}</p>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Timeline</label>
                      <div className="flex items-center">
                        <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getTimelineBadgeColor(selectedLead.timeline, selectedLead.timeline_date)}`}>
                          {selectedLead.timeline_date ? formatTimelineDate(selectedLead.timeline_date) : selectedLead.timeline}
                        </span>
                        {selectedLead.timeline_date && (
                          <span className="ml-2 text-xs text-gray-500">
                            (Originally: {selectedLead.timeline})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Lead Source Section */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Lead Source</label>
                      <div className="flex items-center">
                        {getSourceBadge(selectedLead.source)}
                      </div>
                    </div>

                    {/* Email Status Section */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Email Status</label>
                      <div className="flex items-center">
                        {getEmailStatusBadge(selectedLead.email_status || 'subscribed')}
                      </div>
                      {selectedLead.email_status === 'unsubscribed' && selectedLead.unsubscribed_at && (
                        <div className="mt-2 text-xs text-gray-500">
                          <div>Unsubscribed: {formatDate(selectedLead.unsubscribed_at)}</div>
                          {selectedLead.unsubscribe_reason && (
                            <div>Reason: {selectedLead.unsubscribe_reason}</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Conversion Tracking Section */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Conversion Status</label>
                      <div className="flex items-center">
                        {getConversionStatusBadge(selectedLead.conversion_status || 'pending')}
                      </div>
                    </div>

                    {selectedLead.conversion_status === 'enrolled' && (
                      <>
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Enrollment Date</label>
                          <p className="text-lg font-medium text-gray-900">
                            {selectedLead.enrollment_date ? formatDate(selectedLead.enrollment_date) : 'Not provided'}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Payment Amount</label>
                          <p className="text-lg font-medium text-gray-900">
                            {selectedLead.payment_amount ? `$${selectedLead.payment_amount.toLocaleString()}` : 'Not provided'}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Payment Period</label>
                          <p className="text-lg font-medium text-gray-900">
                            {selectedLead.payment_period || 'Not provided'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Parent Name *</label>
                      <input
                        type="text"
                        value={editFormData?.parent_name || ''}
                        onChange={(e) => handleEditInputChange('parent_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter parent's full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input
                        type="email"
                        value={editFormData?.email || ''}
                        onChange={(e) => handleEditInputChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="parent@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={editFormData?.phone || ''}
                        onChange={(e) => handleEditInputChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Timeline</label>
                      <select
                        value={editFormData?.timeline || 'Just exploring'}
                        onChange={(e) => handleEditInputChange('timeline', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {timelineOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
                      <select
                        value={editFormData?.source || 'chatbot'}
                        onChange={(e) => handleEditInputChange('source', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="chatbot">💬 Chatbot</option>
                        <option value="quiz">🎯 Quiz</option>
                        <option value="enroll_form">📝 Contact Form</option>
                        <option value="newsletter_form">📧 Newsletter</option>
                        <option value="manual">✍️ Manual Entry</option>
                        <option value="other">❓ Other</option>
                      </select>
                    </div>
                  </div>
                )}

                {isEditing && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Specific Start Date (optional)</label>
                    <input
                      type="date"
                      value={editFormData?.timeline_date || ''}
                      onChange={(e) => handleEditInputChange('timeline_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

              </div>

              {/* Children Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-sm">👶</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Children Information
                      </h3>
                      <p className="text-xs text-gray-500">
                        {(editFormData?.children || getChildren(selectedLead)).length} {(editFormData?.children || getChildren(selectedLead)).length === 1 ? 'child' : 'children'} requiring support
                      </p>
                    </div>
                  </div>
                  {isEditing && (
                    <button
                      onClick={addChild}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                    >
                      + Add Child
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {(editFormData?.children || getChildren(selectedLead)).map((child: Child, index: number) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-2">
                            <span className="text-xs font-semibold text-indigo-600">{index + 1}</span>
                          </div>
                          <div>
                            {isEditing ? (
                              <input
                                type="text"
                                value={child.name}
                                onChange={(e) => updateChild(index, 'name', e.target.value)}
                                className="text-base font-semibold text-gray-900 bg-white border border-gray-300 rounded px-2 py-1"
                                placeholder="Child's name"
                              />
                            ) : (
                              <h4 className="text-base font-semibold text-gray-900">{child.name}</h4>
                            )}
                            <p className="text-xs text-gray-500">Child Profile</p>
                          </div>
                        </div>
                        {isEditing && (
                          <button
                            onClick={() => removeChild(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Age</label>
                          {isEditing ? (
                            <input
                              type="number"
                              value={child.age || ''}
                              onChange={(e) => updateChild(index, 'age', parseInt(e.target.value) || null)}
                              className="w-full px-3 py-1 border border-gray-300 rounded-md text-base"
                              placeholder="Age"
                            />
                          ) : (
                            <p className="text-base font-medium text-gray-900">{child.age || 'Not specified'}</p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Grade Level</label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={child.grade || ''}
                              onChange={(e) => updateChild(index, 'grade', e.target.value)}
                              className="w-full px-3 py-1 border border-gray-300 rounded-md text-base"
                              placeholder="Grade level"
                            />
                          ) : (
                            <p className="text-base font-medium text-gray-900">{child.grade || 'Not specified'}</p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Special Needs</label>
                          {isEditing ? (
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-2">
                                {specialNeedsOptions.map((option) => (
                                  <label key={option} className="flex items-center text-sm">
                                    <input
                                      type="checkbox"
                                      checked={child.special_needs?.includes(option) || false}
                                      onChange={(e) => {
                                        const currentNeeds = child.special_needs || [];
                                        if (e.target.checked) {
                                          updateChild(index, 'special_needs', [...currentNeeds, option]);
                                        } else {
                                          updateChild(index, 'special_needs', currentNeeds.filter((n: string) => n !== option));
                                        }
                                      }}
                                      className="mr-1"
                                    />
                                    {option}
                                  </label>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {child.special_needs && child.special_needs.length > 0 ? (
                                child.special_needs.map((need: string, needIndex: number) => (
                                  <span
                                    key={needIndex}
                                    className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700 border border-red-200"
                                  >
                                    {need}
                                  </span>
                                ))
                              ) : (
                                <span className="text-sm text-gray-500 italic">None specified</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {(child.current_support && child.current_support.length > 0) || isEditing ? (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Current Support Services</label>
                          {isEditing ? (
                            <div className="flex flex-wrap gap-2">
                              {supportServicesOptions.map((option) => (
                                <label key={option} className="flex items-center text-sm">
                                  <input
                                    type="checkbox"
                                    checked={child.current_support?.includes(option) || false}
                                    onChange={(e) => {
                                      const currentSupport = child.current_support || [];
                                      if (e.target.checked) {
                                        updateChild(index, 'current_support', [...currentSupport, option]);
                                      } else {
                                        updateChild(index, 'current_support', currentSupport.filter((s: string) => s !== option));
                                      }
                                    }}
                                    className="mr-1"
                                  />
                                  {option}
                                </label>
                              ))}
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {child.current_support?.map((support: string, supportIndex: number) => (
                                <span
                                  key={supportIndex}
                                  className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 border border-blue-200"
                                >
                                  {support}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))}

                  {(editFormData?.children || getChildren(selectedLead)).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No children added yet
                      {isEditing && (
                        <div className="mt-2">
                          <button
                            onClick={addChild}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                          >
                            Add First Child
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Quiz Results Section - Only for quiz leads */}
              {selectedLead.source === 'quiz' &&
               selectedLead.quiz_profile &&
               selectedLead.quiz_dimension_scores && (
                <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 shadow-sm">
                  {!isQuizInsightsExpanded ? (
                    /* Compact Summary View */
                    <div>
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-sm">🎯</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Quiz Results</h3>
                          <p className="text-xs text-gray-500">
                            {getChildren(selectedLead)[0]?.name || 'child'}
                          </p>
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-500 uppercase mb-1">Assigned Profile</p>
                        <p className="text-base font-semibold text-gray-900">
                          {selectedLead.quiz_profile.name}
                        </p>
                      </div>

                      {/* Top 3 Dimension Scores */}
                      <div className="space-y-2 mb-3">
                        <p className="text-sm font-medium text-gray-500 uppercase">Top Dimensions</p>
                        {(() => {
                          const scores = parseQuizDimensionScores(selectedLead.quiz_dimension_scores);
                          const dimensionNames: Record<string, string> = {
                            sensory: 'Sensory',
                            structure: 'Structure',
                            pacing: 'Pacing',
                            confidence: 'Confidence',
                            interest: 'Interest'
                          };
                          return Object.entries(scores.percentages)
                            .sort((a, b) => (b[1] as number) - (a[1] as number))
                            .slice(0, 3)
                            .map(([dimension, percentage]) => {
                              const pct = percentage as number;
                              return (
                                <div key={dimension} className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">{dimensionNames[dimension]}</span>
                                  <div className="flex items-center">
                                    <div className="w-32 h-3 bg-gray-200 rounded-full mr-2">
                                      <div
                                        className="h-3 rounded-full"
                                        style={{
                                          width: `${pct}%`,
                                          backgroundColor: widgetSettings.primary_color || '#4F46E5'
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 w-12 text-right">{pct}%</span>
                                  </div>
                                </div>
                              );
                            });
                        })()}
                      </div>

                      <button
                        onClick={() => setIsQuizInsightsExpanded(true)}
                        className="w-full flex items-center justify-center p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium text-gray-700"
                      >
                        View Full Quiz Results ▼
                      </button>
                    </div>
                  ) : (
                    /* Full Quiz Results View with Detailed Answers */
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">🎯</span>
                          <h3 className="text-sm font-semibold text-gray-900">Detailed Quiz Responses</h3>
                        </div>
                        <button
                          onClick={() => setIsQuizInsightsExpanded(false)}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Collapse ▲
                        </button>
                      </div>

                      {/* Quiz Answers Section with full Q&A details */}
                      <QuizAnswersSection leadId={selectedLead.id} />
                    </div>
                  )}
                </div>
              )}

              {/* Lead Metrics */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-sm">📊</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Lead Analytics</h3>
                    <p className="text-xs text-gray-500">Scoring and engagement metrics</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Lead Score</label>
                    <div className="flex items-center justify-center">
                      <span className={`inline-flex px-3 py-1 text-base font-bold rounded-full ${getScoreBadgeColor(selectedLead.lead_score)}`}>
                        {selectedLead.lead_score}
                      </span>
                      <span className="ml-1 text-xs text-gray-500">pts</span>
                    </div>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Created</label>
                    <p className="text-sm font-medium text-gray-900">{formatDate(selectedLead.created_at)}</p>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Updated</label>
                    <p className="text-sm font-medium text-gray-900">{selectedLead.updated_at ? formatDate(selectedLead.updated_at) : 'Never'}</p>
                  </div>
                </div>
              </div>

              {/* Conversion Tracking Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-sm">💰</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Conversion Tracking</h3>
                    <p className="text-xs text-gray-500">Sales pipeline and enrollment status</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Conversion Status</label>
                    {isEditing ? (
                      <select
                        value={editFormData?.conversion_status || 'pending'}
                        onChange={(e) => handleEditInputChange('conversion_status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="interested">Interested</option>
                        <option value="enrolled">Enrolled</option>
                        <option value="lost">Lost</option>
                      </select>
                    ) : (
                      <div className="flex items-center">
                        {getConversionStatusBadge(selectedLead.conversion_status || 'pending')}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Enrollment Date</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editFormData?.enrollment_date || ''}
                        onChange={(e) => handleEditInputChange('enrollment_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <p className="text-sm text-gray-900 mt-2">
                        {selectedLead.enrollment_date || 'Not set'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                    {isEditing ? (
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData?.payment_amount || ''}
                          onChange={(e) => handleEditInputChange('payment_amount', e.target.value)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-gray-900 mt-2">
                        {selectedLead.payment_amount ? `$${Number(selectedLead.payment_amount).toFixed(2)}` : 'Not set'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Period</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData?.payment_period || ''}
                        onChange={(e) => handleEditInputChange('payment_period', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Monthly, Annual, One-time"
                      />
                    ) : (
                      <p className="text-sm text-gray-900 mt-2">
                        {selectedLead.payment_period || 'Not set'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-sm">📝</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
                      <p className="text-xs text-gray-500">Parent concerns and administrative notes</p>
                    </div>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-3">
                    <textarea
                      value={editFormData.biggest_concerns || ''}
                      onChange={(e) => setEditFormData((prev: any) => ({ ...prev, biggest_concerns: e.target.value }))}
                      placeholder="Add parent concerns or administrative notes about this lead..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      rows={4}
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedLead.biggest_concerns ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{selectedLead.biggest_concerns}</p>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        <p className="text-sm">No notes added yet</p>
                        {!isEditing && (
                          <p className="text-xs mt-1">Click Edit to add notes</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Segment Tags */}
              {selectedLead.segment_tags && selectedLead.segment_tags.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-sm">🏷️</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Lead Segments</h3>
                      <p className="text-xs text-gray-500">Automatically assigned categories</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedLead.segment_tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Conversation Viewer Modal */}
      {showConversationViewer && (
        <ConversationViewer
          leadId={showConversationViewer}
          onClose={() => setShowConversationViewer(null)}
        />
      )}

      {/* Manual Lead Form Modal */}
      {showManualLeadForm && (
        <ManualLeadForm
          onSuccess={handleManualLeadSuccess}
          onCancel={() => setShowManualLeadForm(false)}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <BulkLeadImport
          onSuccess={handleBulkImportSuccess}
          onCancel={() => setShowBulkImportModal(false)}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Export All Leads</h3>
            <p className="text-gray-600 mb-4">
              Export all leads with complete data including conversation history, children information, and resource interactions.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Export Format:
              </label>
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="csv">CSV (Spreadsheet)</option>
                <option value="json">JSON (Structured Data)</option>
              </select>
            </div>

            <div className="text-sm text-gray-500 mb-4">
              {exportFormat === 'csv' ? (
                <div>
                  <strong>CSV Format includes:</strong>
                  <ul className="list-disc list-inside mt-1">
                    <li>Lead contact information</li>
                    <li>All children's details</li>
                    <li>Conversation transcripts</li>
                    <li>Segment assignments</li>
                    <li>Resource download history</li>
                    <li>Enrollment status & dates</li>
                    <li>Payment amounts & periods</li>
                    <li>Parent concerns & notes</li>
                  </ul>
                </div>
              ) : (
                <div>
                  <strong>JSON Format includes:</strong>
                  <ul className="list-disc list-inside mt-1">
                    <li>Complete structured data</li>
                    <li>Nested conversation objects</li>
                    <li>Full metadata and timestamps</li>
                    <li>Developer-friendly format</li>
                  </ul>
                </div>
              )}
            </div>

            {/* Column Selection for CSV */}
            {exportFormat === 'csv' && availableColumns.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Columns to Export:
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowColumnSelection(!showColumnSelection)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    {showColumnSelection ? 'Hide' : 'Customize'} Columns
                  </button>
                </div>

                {showColumnSelection && (
                  <div className="border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <div className="text-xs text-gray-500 mb-3">
                      {selectedColumns.length} of {availableColumns.flatMap(g => g.columns).length} columns selected
                    </div>

                    <div className="space-y-4">
                      {availableColumns.map((group) => {
                        const groupColumns = group.columns;
                        const allSelected = groupColumns.every(col => selectedColumns.includes(col));
                        const someSelected = groupColumns.some(col => selectedColumns.includes(col));

                        return (
                          <div key={group.group} className="border border-gray-100 rounded p-3">
                            <div className="flex items-center mb-2">
                              <input
                                type="checkbox"
                                id={`group-${group.group}`}
                                checked={allSelected}
                                ref={(input) => {
                                  if (input) input.indeterminate = someSelected && !allSelected;
                                }}
                                onChange={() => handleGroupToggle(group)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor={`group-${group.group}`}
                                className="ml-2 text-sm font-medium text-gray-900"
                              >
                                {group.group} ({groupColumns.filter(col => selectedColumns.includes(col)).length}/{groupColumns.length})
                              </label>
                            </div>

                            <div className="ml-6 grid grid-cols-2 gap-2">
                              {groupColumns.map((column) => (
                                <div key={column} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id={`col-${column}`}
                                    checked={selectedColumns.includes(column)}
                                    onChange={() => handleColumnToggle(column)}
                                    className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <label
                                    htmlFor={`col-${column}`}
                                    className="ml-2 text-xs text-gray-700"
                                  >
                                    {column.replace(/_/g, ' ')}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowExportModal(false);
                  setShowColumnSelection(false);
                  setSelectedColumns([]);
                  setAvailableColumns([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                onClick={performExport}
                disabled={isExporting}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Exporting...
                  </>
                ) : (
                  <>📤 Export {exportFormat.toUpperCase()}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Segment Management Modal */}
      {segmentModalLead && (
        <SegmentManagementModal
          lead={segmentModalLead}
          segments={segments}
          onClose={() => setSegmentModalLead(null)}
          onUpdate={fetchLeads}
        />
      )}
    </div>
  );
};

export default LeadsTable;